import { fmtNum } from '../../utils/formatArcaNumbers';

/**
 * Cómo se pinta un plan en el selector de vista: su logo y su saldo.
 *
 * Vive aparte de los componentes a propósito: escritorio y móvil pintan la
 * misma lista, y cuando cada uno resolvía esto por su cuenta acababan
 * divergiendo (el `renderPlanItem` duplicado de la PR #470).
 */

/** Icono de un plan cuya gestora no reconocemos. Nunca se deja el hueco vacío. */
export const GENERIC_PLAN_LOGO = '/imgs/gestoras/generico.svg';

/**
 * Gestoras con logo propio. La clave es lo que hay que encontrar DENTRO del
 * nombre que sirve el catálogo, no el nombre entero: ahí llega la razón social
 * completa ("CASER PENSIONES, ENTIDAD GESTORA DE FONDOS"), así que comparar por
 * igualdad exacta dejaría sin logo a casi todos.
 */
const MANAGER_LOGOS: ReadonlyArray<readonly [string, string]> = [
	['indexa', '/imgs/gestoras/indexa.png'],
	['horos', '/imgs/gestoras/horos.png'],
	['magallanes', '/imgs/gestoras/magallanes.png'],
	['cobas', '/imgs/gestoras/cobas.png'],
	['cecabank', '/imgs/gestoras/cecabank.png'],
	// 🔴 Caser va la ÚLTIMA a propósito: es la depositaria de casi todo, así que
	// su nombre aparece en planes que son de otra casa («Caser Magallanes, PP»).
	// Gana la marca más específica; Caser sólo cuando no hay ninguna otra.
	['caser', '/imgs/gestoras/caser.png']
];

/** Sin acentos, sin mayúsculas y sin espacios de sobra, para poder comparar. */
const normalize = (value: string): string =>
	value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const logoIn = (value: string | null | undefined): string | undefined => {
	if (!value) return undefined;
	const haystack = normalize(value);
	return MANAGER_LOGOS.find(([needle]) => haystack.includes(needle))?.[1];
};

/**
 * El logo con el que se distingue un plan en la lista.
 *
 * 🔴 Manda el NOMBRE DEL PLAN, y sólo después la gestora. En los datos reales
 * la gestora legal es la misma para casi todo —Caser es la depositaria—, así
 * que mirar sólo ahí le pintaba el mismo logo a los tres planes distintos de
 * una misma persona. Lo que de verdad los separa está en el nombre comercial
 * («Horos Internacional, PP», «Caser Magallanes, PP»).
 *
 * Los dos campos son nullable en el contrato, y una entidad nueva puede
 * aparecer cualquier día: todo eso cae en el icono genérico antes que dejar el
 * hueco vacío.
 */
export const resolvePlanLogo = (
	planDisplayName: string | null | undefined,
	managerName: string | null | undefined
): string => logoIn(planDisplayName) ?? logoIn(managerName) ?? GENERIC_PLAN_LOGO;

/** Lo que se pinta cuando el saldo todavía no se sabe. */
const UNKNOWN_BALANCE = '—';

/**
 * El saldo del plan, de CÉNTIMOS a euros.
 *
 * 🔴 `null` no es cero: significa «todavía no se sabe» — el plan es reciente, o
 * su dinero no se puede atribuir a una fila concreta, o la foto diaria aún no ha
 * pasado por él. Pintarlo como 0,00 € le diría a alguien que su plan está
 * vacío, así que va un guion. Un 0 sólo se pinta cuando el dato existe y dice
 * cero.
 */
export const formatPlanBalance = (vestedRightsCents: number | null | undefined): string =>
	vestedRightsCents === null || vestedRightsCents === undefined
		? UNKNOWN_BALANCE
		: `${fmtNum(vestedRightsCents / 100)} €`;
